import GridViewIcon from '@mui/icons-material/GridView';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { type Layout } from 'react-grid-layout';

import {
  layoutsEqual,
  TEMPLATE_ORDER,
  TEMPLATES,
  templateGlyph,
  type TemplateId,
} from './templates.js';
import { useUserPreference } from '../../hooks/use-user-preference.js';

interface Props {
  scope: string;
  tileIds: string[];
}

const LayoutTemplatePicker = ({ scope, tileIds }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pendingId, setPendingId] = useState<TemplateId | null>(null);
  const [savedLayout, setSavedLayout] = useUserPreference<Layout[]>(`layout:${scope}`, []);

  const close = () => setAnchor(null);

  const pick = (id: TemplateId) => {
    const next = TEMPLATES[id].build(tileIds);
    // Bare template? Apply immediately. Customised layout? Confirm overwrite.
    const autoLayout = TEMPLATES.auto.build(tileIds);
    const isFreshOrAuto =
      savedLayout.length === 0 || layoutsEqual(savedLayout, autoLayout);
    if (isFreshOrAuto || layoutsEqual(savedLayout, next)) {
      setSavedLayout(next);
      close();
      return;
    }
    setPendingId(id);
    close();
  };

  const confirmReplace = () => {
    if (!pendingId) return;
    setSavedLayout(TEMPLATES[pendingId].build(tileIds));
    setPendingId(null);
  };

  return (
    <>
      <Tooltip title="Layout template">
        <IconButton color="inherit" onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}>
          <GridViewIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {TEMPLATE_ORDER.map((id) => (
          <MenuItem key={id} onClick={() => pick(id)} sx={{ minWidth: 220, py: 1 }}>
            <ListItemIcon>
              <TemplateGlyph id={id} />
            </ListItemIcon>
            <ListItemText primary={TEMPLATES[id].label} />
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={Boolean(pendingId)} onClose={() => setPendingId(null)} maxWidth="xs">
        <DialogTitle>Replace your layout?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apply <b>{pendingId ? TEMPLATES[pendingId].label : ''}</b>?
            Your current custom layout for this view will be replaced.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingId(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmReplace}>Replace</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TemplateGlyph = ({ id }: { id: TemplateId }) => {
  const theme = useTheme();
  const rects = templateGlyph(id);
  const W = 28;
  const H = 18;
  return (
    <Box
      component="svg"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      sx={{ width: W, height: H, display: 'block' }}
    >
      <rect x={0} y={0} width={1} height={1} fill={theme.palette.action.hover} />
      {rects.map((r, i) => (
        <rect
          key={i}
          x={r.x + 0.01}
          y={r.y + 0.01}
          width={Math.max(0, r.w - 0.02)}
          height={Math.max(0, r.h - 0.02)}
          fill={theme.palette.primary.main}
          fillOpacity={0.75}
          rx={0.02}
        />
      ))}
    </Box>
  );
};

export default LayoutTemplatePicker;
